import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal,
  Box,
  Typography,
  TextareaAutosize,
  FormControlLabel,
  Button,
  Stack,
  TextField
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useOkta } from '../../../hooks/oktaHook';
import ChipInput from '../Chip';
import IOSSwitch from '../IOSSwitch';
import { createSubTask, deleteTask } from '../../../pages/Calendar/service';
import { fetchAllMembers } from '../../../pages/HomePage/service';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import attachmentImage from '../../../assests/images/attachmentImage.svg';

const TaskModal = (props) => {
  const { open, handleClose, selectedSubTaskAction, getDataList } = props;
  const { data, prinicpalTaskName, action } = selectedSubTaskAction;
  const initialTaskState = {
    startDate: null,
    endDate: null,
    taskName: '',
    taskDescription: '',
    assignedTo: [],
    outlookNotifications: false
  };
  const [taskData, setTaskData] = useState(initialTaskState);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { userDetails } = useOkta();
  const { email } = userDetails;
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const { startDate, endDate, taskName, taskDescription, assignedTo, outlookNotifications } =
    taskData;

  const fetchData = async () => {
    try {
      const response = await fetchAllMembers(selectedBu, selectedBu);
      const leads = response?.listOfLead?.map((v) => v?.userEmail) || [];
      const members = response?.listOfMember?.map((v) => v?.userEmail) || [];
      setTeamMembers([...leads, ...members]);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
      if (action === 'edit') {
        let obj = {
          startDate: data?.startDate,
          endDate: data?.endDate,
          taskName: data?.subTaskName,
          taskDescription: data?.description,
          assignedTo: data?.assignedTo?.map((li) => li?.assigneeEmail),
          outlookNotifications: data?.outlookFlag
        };
        setTaskData(obj);
      } else {
        setTaskData(initialTaskState);
      }
    }
  }, [open, selectedBu]);

  const handleChange = (value, keyName) => {
    taskData[keyName] = value;
    setTaskData({ ...taskData, keyName: value });
  };

  const addSubTask = () => {
    let key = action === 'add' ? 'principleTaskId' : 'id';
    let value = action === 'add' ? data : data?.id;
    let payloadObj = {
      [key]: value,
      subTaskName: taskName,
      description: taskDescription,
      startDate: startDate,
      endDate: endDate,
      assignedTo: assignedTo,
      outlookFlag: outlookNotifications
    };
    createSubTask(payloadObj, email, action).then(() => {
      getDataList();
    });
    handleClose();
  };

  const handleDelete = () => {
    deleteTask(data?.id, outlookNotifications).then((res) => {
      getDataList();
    });
    setShowConfirmationModal(!showConfirmationModal);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '40px',
            gap: '10px',
            position: 'absolute',
            width: '561px',
            //   height: '648px',
            left: 'calc(50% - 561px/2 + 10.5px)',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#FBFCFE',
            border: '10px solid #D7E5F8',
            boxShadow: '0px 2px 24px 8px rgba(0, 92, 188, 0.12)',
            borderRadius: '8px',
            //   maxHeight: '90vh',
            overflowY: 'auto',
            scrollbarWidth: 'thin', // For Firefox
            scrollbarColor: 'rgba(0,0,0,0.2) transparent', // For Firefox
            '&::WebkitScrollbar': {
              width: '6px'
            },
            '&::WebkitScrollbarTrack': {
              background: 'transparent'
            },
            '&::WebkitScrollbarThumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '3px'
            }
          }}>
          <Typography variant="h6" sx={{ alignSelf: 'flex-start' }} data-testid="header-name">
            {`${action === 'add' ? 'Create New' : 'Edit'} Task`}
          </Typography>
          <Typography
            variant="h6"
            sx={{ alignSelf: 'flex-start', fontWeight: 500, fontSize: '16px' }}
            data-testid="principal-step-name">
            <img src={attachmentImage} style={{ paddingRight: '10px' }} />
            {prinicpalTaskName}
          </Typography>
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Typography variant="h10" sx={{ fontSize: '14px' }}>
              Task Name *
            </Typography>
            <TextField
              data-testid="taskname-input"
              sx={{
                marginTop: 0,
                height: 32, // Set the height
                '& .MuiInputBase-root': {
                  height: 32 // Ensure the input height is also 32px
                },
                '& .MuiOutlinedInput-root': {
                  height: 32 // For outlined variant, ensure root height is set
                }
              }}
              onChange={(e) => handleChange(e.target.value, 'taskName')}
              value={taskName}
            />
            <Typography variant="h10" sx={{ fontSize: '14px' }}>
              Timeline *
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box fullwidth>
                <Stack direction="row" spacing={2}>
                  <DatePicker
                    value={dayjs(startDate)}
                    onChange={(newValue) =>
                      handleChange(new Date(newValue).toLocaleDateString('en-CA'), 'startDate')
                    }
                    renderInput={(params) => <TextField data-testid="start-date" {...params} />}
                    sx={{
                      marginTop: 0,
                      width: '225px',
                      height: '36px',
                      fontSize: '14px',
                      '& input': { padding: '5px' } // Specific input styling
                    }}
                  />
                  <DatePicker
                    value={dayjs(endDate)}
                    onChange={(newValue) =>
                      handleChange(new Date(newValue).toLocaleDateString('en-CA'), 'endDate')
                    }
                    renderInput={(params) => <TextField data-testid="end-date" {...params} />}
                    sx={{
                      marginTop: 0,
                      width: '225px',
                      height: '36px',
                      fontSize: '14px',
                      '& input': { padding: '5px' } // Specific input styling
                    }}
                  />
                </Stack>
              </Box>
            </LocalizationProvider>

            <Typography variant="h10" sx={{ marginTop: '24px', fontSize: '14px' }}>
              Assign
            </Typography>
            <ChipInput
              value={assignedTo}
              onChange={(assignees) => handleChange(assignees, 'assignedTo')}
              teamMembers={teamMembers}
            />
            <Typography variant="h10" sx={{ fontSize: '14px' }}>
              Notes
            </Typography>
            <TextareaAutosize
              data-testid="description-input"
              id="description"
              placeholder="Type here"
              value={taskDescription}
              onChange={(event) => handleChange(event.target.value, 'taskDescription')}
              rows={4}
              style={{
                width: '100%',
                minHeight: '124px',
                resize: 'both',
                boxSizing: 'border-box',
                borderColor: 'rgba(192, 192, 192, 1)'
              }}
            />
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h10">Outlook Notifications</Typography>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={outlookNotifications}
                    onChange={(event) => handleChange(event.target.checked, 'outlookNotifications')}
                  />
                }
                label={outlookNotifications ? 'Yes' : 'No'}
                labelPlacement="end"
                sx={{ marginLeft: '4px', gap: '2px' }}
              />
            </Stack>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                marginRight: '8px'
              }}>
              {action === 'edit' && (
                <Button
                  data-testid="delete-button"
                  variant="text"
                  color="primary"
                  onClick={() => {
                    handleClose();
                    setShowConfirmationModal(!showConfirmationModal);
                  }}
                  sx={{
                    marginRight: '16px',
                    border: 'none',
                    backgroundColor: '#fff',
                    boxShadow: 'none', // Remove box-shadow
                    '&:hover': {
                      backgroundColor: 'transparent' // Remove the background on hover
                    },
                    '&:focus': {
                      backgroundColor: 'transparent', // Remove the background on focus
                      outline: 'none' // Remove focus outline if desired
                    }
                  }}>
                  Delete
                </Button>
              )}
              <Button
                data-testid="cancel-button"
                variant="outlined"
                color="primary"
                onClick={() => handleClose()}
                sx={{ marginRight: '16px' }}>
                Cancel
              </Button>
              <Button
                data-testid="save-button"
                variant="contained"
                color="primary"
                onClick={() => addSubTask()}
                // endIcon={<CircularProgress size={20} color="inherit" />}
                disabled={
                  !taskData?.startDate ||
                  !taskData?.endDate ||
                  taskData?.startDate > taskData?.endDate ||
                  taskData?.taskName === '' ||
                  taskData?.assignedTo?.length === 0
                }>
                Save
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>
      <ConfirmationModal
        open={showConfirmationModal}
        title={'Delete Task'}
        text={`Are you sure you want to delete this task from ${prinicpalTaskName}?`}
        cancelText="Cancel"
        SubmitText={'Delete'}
        onClose={() => {
          setShowConfirmationModal(false);
        }}
        onSubmit={() => handleDelete()}
        textAlign="start"
      />
    </>
  );
};

export default TaskModal;
